import React, {useMemo, useRef, useEffect} from 'react';
import {PdfWrapper} from "./styles";
import DocViewer, {DocViewerRenderers} from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import {ErrorBoundary} from '../error-boundary';
import {useTranslation} from "react-i18next";

interface Props {
    files: File[];
    selectedFile: number;
    onError: () => void;
}

const PdfViewer: React.FC<Props> = (props: Props) => {
    const {files, selectedFile, onError} = props;
    const { t } = useTranslation();

    const documentsRef = useRef<{ uri: string; fileType: string; }[]>([]);
    const prevFilesRef = useRef<File[]>([]);

    const documents = useMemo(() => {
        const currentFiles = files || [];
        const prevFiles = prevFilesRef.current;
        const filesChanged = currentFiles.length !== prevFiles.length ||
            currentFiles.some((f, i) => f !== prevFiles[i]);

        if (filesChanged) {
            // Revoke old URLs
            documentsRef.current.forEach(doc => {
                try {
                    window.URL.revokeObjectURL(doc.uri);
                } catch (e) {}
            });

            // Create new URLs
            documentsRef.current = currentFiles.map(file => ({
                uri: window.URL.createObjectURL(file),
                fileType: file.type.split('/')[2] || 'pdf'
            }));
            prevFilesRef.current = currentFiles;
        }
        return documentsRef.current;
    }, [files]);

    useEffect(() => {
        return () => {
            documentsRef.current.forEach(doc => {
                try {
                    window.URL.revokeObjectURL(doc.uri);
                } catch (e) {}
            });
        };
    }, []);

    return (
        <PdfWrapper key="doc-viewer-wrapper">
            <ErrorBoundary key="doc-viewer-error-boundary" onError={onError} t={t}>
                <DocViewer
                    key={`doc-${selectedFile}`}
                    config={{
                        header: {
                            disableHeader: true,
                            disableFileName: true,
                            retainURLParams: false,
                        }
                    }}
                    activeDocument={selectedFile !== null ? documents[selectedFile] : undefined}
                    documents={documents}
                    pluginRenderers={DocViewerRenderers}
                />
            </ErrorBoundary>
        </PdfWrapper>)
}

export default PdfViewer;
